import { InvestmentScenariiService } from './investment-scenarii.service';
import { ObservableMap } from './initiatives.service';
import { Injectable } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, shareReplay, switchMap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { PerimetersService } from './perimeters.service';
import { EmissionFactorService } from './emission-factor.service';

@Injectable()
export class PerimeterStatsService {

  constructor(
    private api: APIService,
    private investmentScenariiService: InvestmentScenariiService,
    private emissionFactorService: EmissionFactorService,
  ) { }

  private _perimeterStats: ObservableMap<KPI.PerimeterStats> = {};
  private _perimeterStatsDetails: ObservableMap<KPI.PerimeterStatsDetails> = {};
  public perimeterStats(
    perimeter: KPI.Perimeter,
    start_year: number,
    end_year: number,
    details: boolean = false,
  ): Observable<KPI.PerimeterStats | KPI.PerimeterStatsDetails> {
    const key = this.getKey(arguments);
    if (!details && !this._perimeterStats[key]) {
      this._perimeterStats[key] = this.api.get_perimeter_stats({
        buildings: PerimetersService.getBuildingsIds([perimeter]).join(','),
        start_year,
        end_year
      }).pipe(shareReplay(1));
    } else if (details && !this._perimeterStatsDetails[key]) {
      this._perimeterStatsDetails[key] = <Observable<KPI.PerimeterStatsDetails>>this.api.get_perimeter_stats({
        buildings: PerimetersService.getBuildingsIds([perimeter]).join(','),
        start_year,
        end_year,
        details,
      }).pipe(shareReplay(1));
    }

    return details ? this._perimeterStatsDetails[key] : this._perimeterStats[key];
  }

  private _perimeterEnergyStats: ObservableMap<KPI.PerimeterEnergyStats> = {};
  public perimeterEnergyStats(
    perimeter: KPI.Perimeter,
    queryParams: any,
  ): Observable<KPI.PerimeterEnergyStats> {
    const key = this.getKey(arguments);
    if (!this._perimeterEnergyStats[key]) {
      this._perimeterEnergyStats[key] = combineLatest([
        this.emissionFactorService.getSelectedProvider$('energy'),
        this.emissionFactorService.getSelectedTypeOfUse$('energy'),
        this.investmentScenariiService._flaggedScenarioChanged$,
      ]).pipe(
        debounceTime(0),
        switchMap(([provider, type_of_use]) => {
          const params = {
            ...queryParams,
            type_of_use,
          };
          if (provider?.id) {
            params['provider_id'] = provider.id;
          }
          return this.api.get_perimeter_energy_stats(
            perimeter.id,
            params,
          )
        }),
        shareReplay(1),
      );
    }
    return this._perimeterEnergyStats[key];
  }

  private _sitesIds: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _perimeterEnergyIds: Observable<number[]> = this._sitesIds.pipe(
    filter(sites => !!sites),
    distinctUntilChanged(),
    switchMap(sites => this.api.get_perimeter_energy_ids({sites})),
    shareReplay(1)
  );

  public perimeterEnergyIds(sitesIds: number[]): Observable<number[]> {
    const siteIdsString = sitesIds.sort((a, b) => a - b).join(',');
    this._sitesIds.next(siteIdsString);
    return this._perimeterEnergyIds;
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }
}
